import { touch, stopSubmit } from 'redux-form';
import { setError } from '../../actions';

/**
 * Checks if the error is a passenger email validation error
 * @param {Object} error - The error object from the backend API
 * @returns {boolean} True if this is a passenger email validation error
 */
export const isPassengerEmailValidationError = (error) => {
  return error?.details?.message?.includes('passenger.email.invalid') ?? false;
};

/**
 * Checks if the error is a purchaser email validation error
 * @param {Object} error - The error object from the backend API
 * @returns {boolean} True if this is a purchaser email validation error
 */
export const isPurchaserEmailValidationError = (error) => {
  return error?.details?.purchase_attributes?.includes('purchase.email.invalid') ?? false;
};

/**
 * Matches the backend's ancillary unavailability message, capturing the product code.
 * Example: "passenger.0.ancillary.DONAS1.not_available" → captures "DONAS1".
 */
const ANCILLARY_UNAVAILABLE_REGEX = /ancillary\.([^.]+)\.not_available/;

/**
 * Checks if the error is a product unavailability error.
 * Assumes the backend's current contract (code 300): an `errors` array whose messages
 * look like "passenger.N.ancillary.CODE.not_available". Falls back to `details.message`
 * when the errors array is not present.
 * @param {Object} error - The error object from the backend API
 * @returns {boolean} True if this is a product unavailability error
 */
export const isProductUnavailableError = (error) => {
  if (!error) return false;

  // Primary: errors array with ancillary not_available messages
  if (Array.isArray(error.errors) && error.errors.length > 0) {
    return error.errors.some((err) => ANCILLARY_UNAVAILABLE_REGEX.test(err?.message));
  }

  // Fallback: details.message when the errors array is not present
  return ANCILLARY_UNAVAILABLE_REGEX.test(error.details?.message ?? '');
};

/**
 * Extracts unique product codes from a product unavailability error.
 * Iterates the `errors` array (current contract) and falls back to `details.message`
 * when the array is absent or yields no codes.
 * @param {Object} error - The error object from the backend API
 * @returns {Array<string>} Deduplicated array of unavailable product codes
 */
export const extractUnavailableProductCodes = (error) => {
  if (!error) return [];

  const codesSet = new Set();

  // Primary: iterate the full errors array
  if (Array.isArray(error.errors) && error.errors.length > 0) {
    error.errors.forEach((err) => {
      const match = err?.message?.match(ANCILLARY_UNAVAILABLE_REGEX);
      if (match?.[1]) codesSet.add(match[1]);
    });
  }

  // Fallback: details.message when the errors array yielded nothing
  if (codesSet.size === 0 && error.details?.message) {
    const match = error.details.message.match(ANCILLARY_UNAVAILABLE_REGEX);
    if (match?.[1]) codesSet.add(match[1]);
  }

  return [...codesSet];
};

/**
 * Parses backend API errors and converts them to redux-form field errors
 * This utility helps translate server-side validation errors into client-side form errors
 *
 * @param {Object} error - The error object from the backend API
 * @returns {Object|null} Returns form errors object for redux-form or null if no parseable errors
 */
export const parseBackendErrors = (error) => {
  if (!error || !error.details) {
    return null;
  }

  // Check for passenger email validation errors
  if (error.details?.message?.includes('passenger.email.invalid')) {
    const errorMessage = error.details.message;
    const errorArray = errorMessage.split('.');
    const indexError = errorArray[errorArray.length - 1];

    if (indexError) {
      const passengerIndex = parseInt(indexError, 10);

      // Validate that the parsed index is a valid non-negative number
      if (!Number.isNaN(passengerIndex) && passengerIndex >= 0) {
        const formErrors = {
          passengers: [],
        };

        // Create an array with the error at the specific passenger index
        formErrors.passengers[passengerIndex] = {
          email: 'email_valid', // Use existing translation key
        };

        // Return both errors and fields to touch
        return {
          errors: formErrors,
          fieldsToTouch: [`passengers[${passengerIndex}].email`],
        };
      }
    }
  }

  // Check for purchaser email validation errors
  if (error.details?.purchase_attributes?.includes('purchase.email.invalid')) {
    const formErrors = {
      email: 'email_valid', // Use existing translation key
    };

    // Return both errors and fields to touch
    return {
      errors: formErrors,
      fieldsToTouch: ['email'],
    };
  }

  return null;
};

/**
 * Handles email validation errors by parsing them and dispatching appropriate actions
 * @param {Object} params - The parameters for handling the error
 * @param {Object} params.reason - The error object from the backend API
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {string} params.formName - The form name ('passengers' or 'purchaser')
 * @param {number} params.errorCode - The error code to use (202 for passengers, 204 for purchaser)
 * @param {string} params.errorMessage - The error message key ('passenger_email_validation_error' or 'purchaser_email_validation_error')
 * @returns {void}
 */
export const handleEmailValidationError = ({
  reason,
  dispatch,
  formName,
  errorCode,
  errorMessage,
}) => {
  const errorData = parseBackendErrors(reason);
  if (errorData) {
    // Mark fields as touched so errors show
    dispatch(touch(formName, ...errorData.fieldsToTouch));

    // Use stopSubmit to set field-specific errors
    dispatch(stopSubmit(formName, errorData.errors));

    // Also show global error message with appropriate translation
    dispatch(setError(errorCode, errorMessage, 'error', false));
  }
};
